import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { BlockVisibilityForm } from "./BlockVisibilityForm";
import type { PlatformBlock, PlatformBlockComponent } from "../types";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { DialogHeader } from "@/shared/components";
import {
  Archive,
  Html,
  Text,
  Button as ButtonBlock,
  Markdown,
  Faq,
  Banner,
  Redirect,
  ItemsList,
  Partners,
  FastLinksList,
  AppsList,
  Posts,
  Contents,
  Products,
  Services,
  Packages,
  TagCard,
  Tags,
  CustomCover,
  FavoriteContents,
  ContinueWatchingContents,
} from "./blocks";
import { Box } from "@mui/material";

const components: Record<string, PlatformBlockComponent> = {
  ARCHIVE: Archive,
  TEXT: Text,
  HTML: Html,
  BUTTON: ButtonBlock,
  MARKDOWN: Markdown,
  FAQ: Faq,
  BANNER: Banner,
  REDIRECT: Redirect,
  ITEMS_LIST: ItemsList,
  PARTNERS: Partners,
  FAST_LINKS_LIST: FastLinksList,
  APPS_LIST: AppsList,
  POSTS: Posts,
  CONTENTS: Contents,
  PRODUCTS: Products,
  SERVICES: Services,
  PACKAGES: Packages,
  TAG_CARD: TagCard,
  TAGS: Tags,
  CUSTOM_COVER: CustomCover,
  FAVORITE_CONTENTS: FavoriteContents,
  CONTINUE_WATCHING_CONTENTS: ContinueWatchingContents,
};

interface EditBlockFormProps {
  onSubmit: (block: PlatformBlock) => void;
  onCancel?: () => void;
  initialValues: PlatformBlock;
  platformId?: string;
  queries?: Record<string, any>;
  activeTab?: "SETTINGS" | "VISIBILITY";
  cancelText?: string;
  submitText?: string;
  /** Заголовок диалога */
  title?: string;
  /** Закрыть диалог (крестик) */
  onClose?: () => void;
  /** create = в хедере «Назад» и «Создать», edit = только «Сохранить» */
  mode?: "create" | "edit";
}

/**
 * Форма редактирования блока с двумя вкладками:
 * - Settings: редактирование свойств блока
 * - Visibility: настройки видимости
 */
export const EditBlockForm: React.FC<EditBlockFormProps> = ({
  initialValues,
  platformId,
  queries,
  onSubmit,
  onCancel,
  activeTab = "SETTINGS",
  cancelText,
  submitText,
  title,
  onClose,
  mode = "edit",
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"SETTINGS" | "VISIBILITY">(activeTab);

  const Component = initialValues?.component
    ? components[initialValues.component]
    : null;

  const onChangeTab = (
    _event: React.SyntheticEvent,
    value: "SETTINGS" | "VISIBILITY"
  ) => {
    setTab(value);
  };

  const initialFormValues = initialValues
    ? {
        component: initialValues.component,
        props: initialValues.props || {},
        visibilityConditions: initialValues.visibilityConditions || {
          userType: "ALL",
          countries: {
            type: "ALL",
            countryCodes: [],
          },
          applicationPlatforms: {
            type: "ALL",
            platforms: [],
          },
        },
      }
    : null;

  const handleFormSubmit = (values: any) => {
    const savedBlock: PlatformBlock = {
      component: values.component,
      props: values.props || {},
      visibilityConditions: values.visibilityConditions || {
        userType: "ALL",
        countries: {
          type: "ALL",
          countryCodes: [],
        },
        applicationPlatforms: {
          type: "ALL",
          platforms: [],
        },
      },
    };
    onSubmit(savedBlock);
  };

  return (
    <Form
      key={`block-form-${initialValues.component}`}
      mutators={{ ...arrayMutators }}
      onSubmit={handleFormSubmit}
      initialValues={initialFormValues || {}}
      render={({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogHeader
            title={title ?? t("platformBlocks.dialog.title")}
            onClose={onClose}
            actions={
              <>
                {mode === "create" && onCancel && (
                  <Button onClick={onCancel} variant="outlined" type="button">
                    {cancelText ?? t("platformBlocks.dialog.cancel")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={submitting}
                >
                  {submitText ?? t("platformBlocks.dialog.save")}
                </Button>
              </>
            }
          >
            <Box sx={{ mx: -2, mb: -2 }}>
              <Tabs value={tab} onChange={onChangeTab} indicatorColor="primary">
                <Tab
                  label={t("platformBlocks.dialog.tabs.settings")}
                  value="SETTINGS"
                />
                <Tab
                  label={t("platformBlocks.dialog.tabs.visibility")}
                  value="VISIBILITY"
                />
              </Tabs>
            </Box>
          </DialogHeader>
          <DialogContent sx={{ p: 1, minHeight: 400 }}>
            {Component && tab === "SETTINGS" && (
              <Component
                name="props"
                values={(values ?? initialFormValues) as any}
                platformId={platformId}
                queries={queries}
              />
            )}
            {tab === "VISIBILITY" && (
              <BlockVisibilityForm
                block={initialFormValues as any}
                platformId={platformId}
              />
            )}
          </DialogContent>
        </form>
      )}
    />
  );
};
